import { Gender } from '@zorro/clients';
import { formatSsn, padFips } from '@zorro/common-utils';
import { DateUtilInstance, formatDateEnLocale } from '@zorro/shared/formatters';
import {
  YesNo,
  citizenshipStatusLabelConfig,
  employmentTypeLabelConfig,
  maritalStatusLabelConfig,
  wageTypeLabelConfig,
} from '@zorro/types';
import { useMonolithQuery } from '@zorro/web/ui-utils';
import { UseFormReturn } from 'react-hook-form';

import { InsuredEmployeeFormFields } from '../EmployeeFormInputs';
import {
  calculateAgeAtPlanStart,
  getPhoneVariations,
} from './ReadonlyInsuredFormUtils';
import { ReadonlyValuesGrid } from './ReadonlyValuesGrid';

type Props = {
  employeeForm: UseFormReturn<InsuredEmployeeFormFields>;
  onboardingPeriodId?: string;
  shouldShowEmploymentInfo?: boolean;
  targetEnrollmentDate?: DateUtilInstance;
  withAgeAtPlanStart?: boolean;
  employeeZipCode?: string | null;
  employeeFipsCode?: string | null;
};

export const ReadonlyEmployeeForm = ({
  employeeForm,
  onboardingPeriodId,
  shouldShowEmploymentInfo = true,
  targetEnrollmentDate,
  withAgeAtPlanStart = false,
  employeeZipCode,
  employeeFipsCode,
}: Props) => {
  const {
    firstName,
    lastName,
    dateOfBirth,
    phone,
    gender,
    isSmoker,
    isPregnant,
    email,
    personalEmail,
    residentialAddress,
    mailingAddress,
    ssn,
    citizenshipStatus,
    maritalStatus,
    employmentType,
    wageType,
    customClassCategory,
  } = employeeForm.watch();

  const { data: insured } = useMonolithQuery({
    method: 'insuredControllerFindInsuredForPeriod',
    params: onboardingPeriodId && [onboardingPeriodId],
  });

  // Prefer the persisted enrollment record; fall back to the form in global mode
  const { fipsCode, zipCode } = insured?.employee ?? {
    fipsCode: employeeFipsCode,
    zipCode: employeeZipCode,
  };

  const { data: counties } = useMonolithQuery({
    method: 'addressControllerGetCountiesByZipCode',
    params: zipCode && [Number(zipCode)],
  });
  // Some stored codes lost their leading zero (see padFips)
  const paddedFipsCode = padFips(fipsCode ?? '');
  const county = counties?.find(
    (record) => record.fipsCode === paddedFipsCode
  )?.county;

  const phoneVariations = getPhoneVariations(phone);

  const ageAtPlanStart = calculateAgeAtPlanStart(
    dateOfBirth,
    targetEnrollmentDate
  );

  return (
    <ReadonlyValuesGrid
      items={[
        { id: 'employee-info', title: 'Employee info' },
        {
          id: 'firstName',
          label: 'First name',
          value: firstName,
          masked: true,
          copyValue: firstName,
        },
        {
          id: 'lastName',
          label: 'Last name',
          value: lastName,
          masked: true,
          copyValue: lastName,
        },
        {
          id: 'dateOfBirth',
          label: 'Date of birth',
          value: dateOfBirth ? formatDateEnLocale(dateOfBirth) : undefined,
          masked: true,
          copyValue: dateOfBirth ? formatDateEnLocale(dateOfBirth) : undefined,
        },
        ...(withAgeAtPlanStart
          ? [
              {
                id: 'ageAtPlanStart',
                label: 'Age at plan start',
                value: ageAtPlanStart,
              },
            ]
          : []),
        {
          id: 'phone',
          label: 'Phone',
          value: phoneVariations.valueForView,
          copyValue: phoneVariations.valueForCopy,
        },
        {
          id: 'gender',
          label: 'Gender',
          value: gender === Gender.MALE ? 'Male' : 'Female',
        },
        {
          id: 'smoker',
          label: 'Smoker',
          value: isSmoker === YesNo.YES ? 'Yes' : 'No',
        },
        ...(gender === Gender.FEMALE
          ? [
              {
                id: 'pregnant',
                label: 'Pregnant',
                value: isPregnant === YesNo.YES ? 'Yes' : 'No',
                span: 12,
              },
            ]
          : []),
        {
          id: 'email',
          label: 'Company email',
          value: email,
          copyValue: email,
        },
        {
          id: 'personal-email',
          label: 'Personal email',
          value: personalEmail,
          copyValue: personalEmail,
        },
        {
          id: 'residential-address',
          label: 'Residential address',
          value: residentialAddress,
          span: 12,
          masked: true,
        },
        {
          id: 'county',
          label: 'County of residence',
          value: county,
          span: 12,
          copyValue: county,
        },
        {
          id: 'mailing-address',
          label: 'Mailing address',
          value: mailingAddress,
          span: 12,
          masked: true,
        },
        {
          id: 'ssn',
          label: 'SSN',
          value: formatSsn(ssn),
          isSensitive: true,
          masked: true,
          copyValue: formatSsn(ssn),
        },
        {
          id: 'citizenship-status',
          label: 'Citizenship status',
          value:
            citizenshipStatus &&
            citizenshipStatusLabelConfig[citizenshipStatus],
        },
        {
          id: 'marital-status',
          label: 'Marital status',
          value: maritalStatus && maritalStatusLabelConfig[maritalStatus],
        },
        ...(shouldShowEmploymentInfo
          ? [
              { id: 'employment-info', title: 'Employment info' },
              {
                id: 'custom-class-category',
                label: 'Custom class category',
                value: customClassCategory,
              },
              {
                id: 'employment-type',
                label: 'Employment type',
                value:
                  employmentType && employmentTypeLabelConfig[employmentType],
              },
              {
                id: 'wage-type',
                label: 'Wage type',
                value: wageType && wageTypeLabelConfig[wageType],
              },
            ]
          : []),
      ]}
    />
  );
};
